import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowTopRightOnSquareIcon,
  ClockIcon,
} from "@heroicons/react/24/solid";
import Link from "next/link";
import { useRouter } from "next/router";
import { Fragment, useState } from "react";
import { useRenewVehicleMutation, Vehicle } from "../graphql/generated/graphql";
import { hasMaxRenewDiscount, hasPlusRenewDiscount } from "../utils/checkout";
import {
  RENEW_MAX_DISCOUNTED,
  RENEW_MAX_STANDARD,
  // RENEW_PLUS_DISCOUNTED,
  // RENEW_PLUS_STANDARD,
  TIERS,
  TIER_KEYS,
} from "../utils/constants";
import { classNames } from "../utils/formatters";
import Spinner from "./Spinner";
import TierBadge from "./TierBadge";

type TProps = {
  vehicle: Vehicle;
  open?: boolean;
  setOpen: (open?: boolean) => void;
};

function RenewVehicleModal({ vehicle, open, setOpen }: TProps) {
  const router = useRouter();

  const {
    _id: vehicleId,
    listingTier,
    previousTier,
    renewCount,
    slug,
  } = vehicle;
  const hasMaxDiscount = hasMaxRenewDiscount(previousTier, renewCount);
  const hasPlusDiscount = hasPlusRenewDiscount(previousTier, renewCount);

  const [renewVehicle, { loading: renewing }] = useRenewVehicleMutation({
    onCompleted: () => {
      router.push(`/autosusados/${slug}`);
    },
  });

  function renewFree() {
    renewVehicle({
      variables: {
        _id: vehicleId,
        tier: TIER_KEYS.FREE,
      },
    });
  }

  function goToCheckout(e) {
    const purchase = e.target.id;
    const query = {
      purchase,
      vehicleId,
    };

    router.push({
      pathname: `/checkout`,
      query,
    });
  }

  function onClick(e) {
    if (e.id === TIER_KEYS.FREE) {
      renewFree();
    } else {
      goToCheckout(e);
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div className="text-center">
                  <Dialog.Title as="h3" className="leading-6 ">
                    Renovar publicación
                  </Dialog.Title>
                </div>
                <div className="mt-2 flex flex-col gap-x-1">
                  <div className="mt-2">
                    <div className="flex gap-x-2">
                      <h2 className="leading-6 font-medium text-gray-900">
                        Renovación
                      </h2>
                      <TierBadge tier={TIER_KEYS.MAX} />
                    </div>
                    <h3 className="font-bold text-2xl">
                      <span
                        className={classNames(
                          hasMaxDiscount ? "line-through text-red-500" : ""
                        )}
                      >
                        {`₡${TIERS[TIER_KEYS.MAX].price}`}
                      </span>{" "}
                      {hasMaxDiscount ? (
                        <span className="">
                          {`₡${RENEW_MAX_DISCOUNTED.price}`}
                        </span>
                      ) : null}
                    </h3>
                    <Link href="/pricing" target="_blank">
                      <span className="text-cyan-500 hover:text-cyan-400 inline-flex gap-x-1 items-center cursor-pointer font-medium">
                        Consultar beneficios
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 " />
                      </span>
                    </Link>
                  </div>
                  <button
                    id={
                      hasMaxDiscount
                        ? RENEW_MAX_DISCOUNTED.id
                        : RENEW_MAX_STANDARD.id
                    }
                    onClick={onClick}
                    className="mt-2 inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-blue-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70"
                  >
                    Renovar MAX
                  </button>
                  {/* <div className="mt-2">
                    <div className="flex gap-x-2">
                      <h2 className="leading-6 font-medium text-gray-900">
                        Renovación
                      </h2>
                      <TierBadge tier={TIER_KEYS.PLUS} />
                    </div>
                    <h3 className="font-bold text-2xl">
                      <span
                        className={classNames(
                          hasPlusDiscount ? "line-through text-red-500" : ""
                        )}
                      >
                        {`₡${TIERS[TIER_KEYS.PLUS].price}`}
                      </span>{" "}
                      {hasPlusDiscount ? (
                        <span className="">
                          {`₡${RENEW_PLUS_DISCOUNTED.price}`}
                        </span>
                      ) : null}
                    </h3>
                    <Link href="/pricing" target="_blank">
                      <span className="text-cyan-500 hover:text-cyan-400 inline-flex gap-x-1 items-center cursor-pointer font-medium">
                        Consultar beneficios
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 " />
                      </span>
                    </Link>
                  </div>

                  <button
                    id={
                      hasMaxDiscount
                        ? RENEW_PLUS_DISCOUNTED.id
                        : RENEW_PLUS_STANDARD.id
                    }
                    onClick={onClick}
                    className="mt-2 inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-green-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70"
                  >
                    Renovar PLUS
                  </button> */}

                  <div className="mt-2">
                    <div className="flex gap-x-2">
                      <h2 className="leading-6 font-medium text-gray-900">
                        Renovación GRATIS
                      </h2>
                    </div>
                    <p className=" text-sm text-gray-500">
                      30 días más sin ningún costo
                    </p>
                  </div>

                  {/* <button className="mt-2 inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-orange-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70">
                    Renovar GRATIS{" "}
                  </button> */}

                  <button
                    type="button"
                    className="mt-2 inline-flex gap-x-1 w-full justify-center rounded-md border border-transparent bg-orange-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:text-sm disabled:opacity-70"
                    disabled={renewing}
                    onClick={renewFree}
                  >
                    {renewing ? <Spinner className="h-5 w-5" /> : null}
                    Renovar GRATIS
                  </button>

                  <button
                    type="button"
                    className="mt-2 inline-flex w-full justify-center rounded-md border border-gray-300 hover:border-gray-400 bg-white px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-600 shadow-sm focus:outline-none focus:ring-2  focus:ring-offset-2 sm:text-sm"
                    onClick={() => setOpen(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default function RenewVehicleButton({
  data,
  loading,
}: {
  data: Vehicle;
  loading?: boolean;
  asIcon?: boolean;
}) {
  const [isRenewModalOpen, setRenewModalOpen] = useState(false);

  function toggleRenewModal() {
    setRenewModalOpen(!isRenewModalOpen);
  }

  return (
    <>
      <button
        onClick={toggleRenewModal}
        disabled={loading}
        className="-ml-px relative inline-flex items-center space-x-2 rounded-md border-2 border-blue-600 bg-blue-600 px-3 sm:px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-500 hover:border-blue-500 disabled:opacity-70"
      >
        <ClockIcon className="h-5 w-5" aria-hidden="true" />
        <span className="hidden sm:inline">Renovar</span>
      </button>
      <RenewVehicleModal
        open={isRenewModalOpen}
        setOpen={setRenewModalOpen}
        vehicle={data}
      />
    </>
  );
}
